import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { LeadflowForm } from "@/components/LeadflowForm";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { site } from "@/lib/site";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      ...routeMeta("/contact"),
      { title: "Contact CCOB And Company PA — Winter Garden CPA Firm" },
      { name: "description", content: "Call (407) 654-3822 or request a consultation. Located at 12200 W Colonial Dr #300B, Winter Garden, FL 34787." },
      { property: "og:title", content: "Contact CCOB And Company PA" },
      { property: "og:description", content: "Schedule a consultation with a CPA in Winter Garden, FL." },
    ],
    links: canonicalLinks("/contact"),
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <>
      <PageHero eyebrow="Contact" title="Let's Talk About Your Finances" subtitle="Call our office first for fastest response, or send us a message and we'll be in touch." image={img.heroContact} />

      <section className="py-24">
        <div className="container-x grid lg:grid-cols-3 gap-6">
          {[
            { I: Phone, t: "Call", v: site.phone, href: `tel:${site.phoneRaw}` },
            { I: Mail, t: "Message", v: "Use our secure contact form", href: "#consultation" },
            { I: MapPin, t: "Office", v: `${site.address1}, ${site.address2}`, href: site.social.maps },
          ].map(({ I, t, v, href }) => (
            <motion.a key={t} href={href} target={t === "Office" ? "_blank" : undefined} rel="noreferrer" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-card border border-border rounded-2xl p-7 hover:border-gold transition flex gap-4">
              <div className="h-12 w-12 rounded-full bg-secondary text-primary grid place-items-center shrink-0"><I className="h-5 w-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-gold">{t}</div>
                <div className="font-display text-lg text-primary mt-1">{v}</div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      <section id="consultation" className="pb-24 scroll-mt-24 lg:scroll-mt-28">
        <div className="container-x grid lg:grid-cols-2 gap-10">
          <div className="bg-card border border-border rounded-2xl p-8">
            <h2 className="font-display text-3xl text-primary">Request a Consultation</h2>
            <div className="gold-line mt-4" />
            <div className="mt-8"><LeadflowForm src="https://leadflow.puulse.ai/ccobandcompany/request-a-consultation" /></div>
          </div>

          <div className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-8">
              <h3 className="font-display text-2xl text-primary">Office Hours</h3>
              <div className="gold-line mt-4" />
              <ul className="mt-5 space-y-2 text-sm">
                {site.hours.map(([d, h]) => (
                  <li key={d} className="flex justify-between gap-4 py-2 border-b border-border last:border-0">
                    <span className="text-muted-foreground flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> {d}</span>
                    <span className="font-medium">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <iframe
                title="Office location"
                src="https://www.google.com/maps?q=12200+W+Colonial+Dr+%23300B+Winter+Garden+FL+34787&output=embed"
                className="w-full aspect-[4/3] border-0"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
